import { Extension } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { v4 as uuid } from "uuid";

/**
 * Interface for UUIDOptions
 * @property attributeName - The name of the attribute to use for the UUID (default: "id")
 * @property types - Array of node types or node groups that should receive UUIDs
 * @property generateID - Function to generate a unique ID (defaults to UUID v4)
 * @property debug - Whether to show visual debugging indicators
 */
export interface UUIDOptions {
  /**
   * Name of the attribute that will store the unique ID
   * @default "id"
   */
  attributeName: string;

  /**
   * Array of node types or node groups that should receive unique IDs
   * For example: ["paragraph", "heading", "block", "inline"]
   * @default []
   */
  types: string[];

  /**
   * Function to generate a unique ID
   * @default uuid()
   */
  generateID: () => string;

  /**
   * Whether to show visual debugging indicators
   * @default false
   */
  debug: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    uuid: {
      /**
       * Ensure all nodes of specified types have unique IDs
       */
      ensureUniqueIDs: () => ReturnType;
    };
  }
}

/**
 * UUIDExtension for Tiptap
 *
 * This extension adds unique IDs to all nodes of specified types or groups.
 * It monitors node creation, splitting, and pasting to ensure all nodes have unique IDs.
 */
export const UUIDExtension = Extension.create<UUIDOptions>({
  name: "uuid",

  addOptions() {
    return {
      attributeName: "id",
      types: [],
      generateID: () => uuid(),
      debug: false,
    };
  },

  addStorage() {
    return {
      initialized: false,
    };
  },

  addGlobalAttributes() {
    return this.options.types.map(type => {
      return {
        types: [type],
        attributes: {
          [this.options.attributeName]: {
            default: null,
            parseHTML: element => element.getAttribute(`${this.options.attributeName}`),
            renderHTML: attributes => {
              if (!attributes[this.options.attributeName]) {
                return {};
              }

              // If debug mode is on, add a class for visual debugging
              if (this.options.debug) {
                return {
                  [`${this.options.attributeName}`]: attributes[this.options.attributeName],
                  class: "tiptap-uuid-debug",
                };
              }

              return {
                [`${this.options.attributeName}`]: attributes[this.options.attributeName],
              };
            },
          },
        },
      };
    });
  },

  addCommands() {
    return {
      ensureUniqueIDs:
        () =>
        ({ state, dispatch }) => {
          // Skip if there's nothing to do
          if (!this.options.types.length) {
            return false;
          }

          const { tr, doc } = state;
          let hasChanged = false;

          doc.descendants((node, pos) => {
            const shouldHaveUUID =
              this.options.types.includes(node.type.name) ||
              (node.type.spec.group &&
                node.type.spec.group.split(" ").some(group => this.options.types.includes(group)));

            if (shouldHaveUUID) {
              if (!node.attrs[this.options.attributeName]) {
                const newId = this.options.generateID();

                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  [this.options.attributeName]: newId,
                });
                hasChanged = true;
              }
            }
          });

          if (hasChanged && dispatch) {
            dispatch(tr);
          }

          return hasChanged;
        },
    };
  },

  onBeforeCreate() {
    this.storage.initialized = false;
  },

  onTransaction() {
    if (this.storage.initialized) {
      return;
    }

    setTimeout(() => {
      this.editor.commands.ensureUniqueIDs();
      this.storage.initialized = true;
    }, 0);
  },

  addProseMirrorPlugins() {
    const { types, attributeName, generateID, debug } = this.options;

    return [
      new Plugin({
        key: new PluginKey("uuid"),

        /**
         * Apply unique IDs to nodes that should have them
         */
        appendTransaction(transactions, _, newState) {
          if (types == null) {
            return null;
          }

          const nodesWithoutIDs: { node: Node; pos: number }[] = [];

          const needsToRun = transactions.some(transaction => {
            return transaction.docChanged;
          });

          if (!needsToRun) {
            return null;
          }

          const ignoredTypes = new Set<string>();
          newState.doc.descendants((node, pos) => {
            const shouldHaveUUID =
              types.includes(node.type.name) ||
              (node.type.spec.group && node.type.spec.group.split(" ").some(group => types.includes(group)));

            if (!shouldHaveUUID) {
              ignoredTypes.add(node.type.name);
            }

            if (shouldHaveUUID && !node.attrs[attributeName]) {
              nodesWithoutIDs.push({ node, pos });
            }
          });

          if (debug) {
            console.log("[UUIDExtension] Ignored types:", ignoredTypes);
          }

          if (!nodesWithoutIDs.length) {
            return null;
          }

          const { tr } = newState;

          nodesWithoutIDs.forEach(({ node, pos }) => {
            const nodeID = generateID();

            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              [attributeName]: nodeID,
            });
          });

          if (debug) {
            console.log(`[UUIDExtension] Added IDs to ${nodesWithoutIDs.length} nodes`);
          }

          return tr;
        },
      }),
    ];
  },
});
